import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MdClose } from 'react-icons/md';
import toast from 'react-hot-toast';
import api from '../lib/axios';
import { endpoints } from '../constants/endpoints';

interface FlagChatModalProps {
  isOpen: boolean;
  visitorId: string;
  onClose: () => void;
  onFlagged: () => void;
}

interface FlagFormValues {
  reason: string;
}

export default function FlagChatModal({
  isOpen,
  visitorId,
  onClose,
  onFlagged,
}: FlagChatModalProps) {
  const queryClient = useQueryClient();

  const { register, handleSubmit, reset } = useForm<FlagFormValues>({
    defaultValues: { reason: '' },
  });

  const flagMutation = useMutation({
    mutationFn: (reason: string) =>
      api.post(endpoints.chat.flag(visitorId), { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visitors'] });
      queryClient.invalidateQueries({ queryKey: ['messages', visitorId] });
      onFlagged();
      toast.success('Chat flagged');
      reset();
      onClose();
    },
    onError: () => {
      toast.error('Failed to flag chat');
    },
  });

  const handleClose = () => {
    if (flagMutation.isPending) return;
    reset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={handleClose} />
      <div className="relative w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Flag chat</h3>
          <button
            onClick={handleClose}
            disabled={flagMutation.isPending}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
          >
            <MdClose size={20} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit((data) => flagMutation.mutate(data.reason))}
        >
          <div>
            <label
              htmlFor="reason"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              Reason (optional)
            </label>
            <textarea
              id="reason"
              rows={4}
              maxLength={500}
              {...register('reason')}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
              placeholder="Why is this chat being flagged?"
            />
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={flagMutation.isPending}
              className="rounded-md px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={flagMutation.isPending}
              className="rounded-md border border-red-300 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
            >
              {flagMutation.isPending ? 'Flagging...' : 'Flag'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
